import type { MobileCall } from './api.js';
import { contactActionLabel, openVCardContact } from './vcard.js';
import { configHilo } from './hilo.js';

export interface CallScreenActions {
  now?: () => Date;
  updateStatus: (status: 'opened' | 'dialer_opened') => Promise<void>;
  openDialer: (url: string) => void;
  onDialerOpened?: () => void;
  recordContactOpened: () => Promise<void>;
  downloadContact: (contents: string, filename: string) => void;
}

interface EventTargetLike {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface VisibilityTargetLike extends EventTargetLike {
  visibilityState: DocumentVisibilityState;
}

export interface CallReturnWatcherOptions {
  windowTarget?: EventTargetLike;
  documentTarget?: VisibilityTargetLike;
  onReturn: () => void;
  now?: () => number;
  minimumReturnDelayMs?: number;
}

export function createCallReturnWatcher(
  options: CallReturnWatcherOptions,
): { arm(): void; stop(): void } {
  const windowTarget = options.windowTarget ?? window;
  const documentTarget = options.documentTarget ?? document;
  const now = options.now ?? (() => Date.now());
  const minimumReturnDelayMs = options.minimumReturnDelayMs ?? 500;
  let armed = false;
  let leftForDialer = false;
  let shown = false;
  let armedAt = 0;

  const maybeReturn = () => {
    if (
      !armed
      || shown
      || documentTarget.visibilityState === 'hidden'
      || (!leftForDialer && now() - armedAt < minimumReturnDelayMs)
    ) return;
    shown = true;
    armed = false;
    options.onReturn();
  };
  const onVisibilityChange: EventListener = () => {
    if (documentTarget.visibilityState === 'hidden') {
      if (armed) leftForDialer = true;
      return;
    }
    maybeReturn();
  };
  const onFocus: EventListener = () => maybeReturn();
  const onPageShow: EventListener = () => maybeReturn();
  const onBlur: EventListener = () => {
    if (armed) leftForDialer = true;
  };

  documentTarget.addEventListener('visibilitychange', onVisibilityChange);
  windowTarget.addEventListener('focus', onFocus);
  windowTarget.addEventListener('pageshow', onPageShow);
  windowTarget.addEventListener('blur', onBlur);

  return {
    arm() {
      armed = true;
      leftForDialer = false;
      shown = false;
      armedAt = now();
    },
    stop() {
      armed = false;
      documentTarget.removeEventListener('visibilitychange', onVisibilityChange);
      windowTarget.removeEventListener('focus', onFocus);
      windowTarget.removeEventListener('pageshow', onPageShow);
      windowTarget.removeEventListener('blur', onBlur);
    },
  };
}

function button(label: string, action: string, className: string) {
  const node = document.createElement('button');
  node.type = 'button';
  node.textContent = label;
  node.dataset.action = action;
  node.className = className;
  return node;
}

export function whatsappChatUrl(phoneNumber: string): string {
  return `https://wa.me/${phoneNumber.replace(/\D/g, '')}`;
}

export function renderCallScreen(
  root: HTMLElement,
  call: MobileCall,
  actions: CallScreenActions,
): void {
  const expired =
    new Date(call.expiresAt).getTime() <= (actions.now ?? (() => new Date()))().getTime();
  root.replaceChildren();
  const card = document.createElement('section');
  card.className = 'card call-card';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow';
  eyebrow.textContent = expired ? 'Solicitud vencida' : 'Llamada lista';
  const title = document.createElement('h1');
  title.textContent = call.customerName;
  const phone = document.createElement('p');
  phone.className = 'phone-number';
  phone.textContent = call.phoneNumber;
  const hint = document.createElement('p');
  hint.className = 'description';
  hint.textContent = expired
    ? 'Esta solicitud ya venció. Vuelve a Bitrix para generar una nueva.'
    : call.dealSelection?.state === 'unavailable'
      ? 'No encontramos un deal activo compatible. Puedes llamar, pero registra el resultado manualmente en Bitrix.'
      : 'Elige cómo llamar. Con WhatsApp se abrirá el chat exacto; ahí toca el ícono del teléfono.';
  const callActions = document.createElement('div');
  callActions.className = 'call-actions';
  const simButton = button('Llamar con mi SIM', 'call', 'primary-button');
  const whatsappButton = button(
    'Llamar por WhatsApp',
    'call-whatsapp',
    'primary-button whatsapp-button',
  );
  const saveButton = button(
    contactActionLabel(call.contactPreviouslyOpened),
    'save-contact',
    'secondary-button',
  );
  const status = document.createElement('p');
  status.className = 'status';
  status.setAttribute('role', 'status');
  simButton.disabled = expired;
  whatsappButton.disabled = expired;
  saveButton.disabled = expired;

  if (!expired) {
    void actions.updateStatus('opened').catch(() => {
      status.textContent = 'No se pudo actualizar el estado, pero aún puedes llamar.';
    });
  }
  let openingCall = false;
  const openCall = async (url: string, pendingMessage: string) => {
    if (openingCall) return;
    openingCall = true;
    simButton.disabled = true;
    whatsappButton.disabled = true;
    status.textContent = pendingMessage;
    try {
      await actions.updateStatus('dialer_opened');
      actions.onDialerOpened?.();
      actions.openDialer(url);
    } catch {
      status.textContent = 'No se pudo abrir la llamada. Intenta nuevamente.';
      openingCall = false;
      simButton.disabled = false;
      whatsappButton.disabled = false;
    }
  };
  simButton.addEventListener('click', () => {
    void openCall(`tel:${call.phoneNumber}`, 'Abriendo el marcador…');
  });
  whatsappButton.addEventListener('click', () => {
    void openCall(
      whatsappChatUrl(call.phoneNumber),
      'Abriendo el chat en WhatsApp…',
    );
  });
  let openingContact = false;
  saveButton.addEventListener('click', async () => {
    if (openingContact) return;
    openingContact = true;
    saveButton.disabled = true;
    status.textContent = 'Preparando la tarjeta de contacto…';
    try {
      const { trackingFailed } = await openVCardContact(call, actions);
      status.textContent = trackingFailed
        ? 'Tarjeta lista. No se pudo guardar el recordatorio en la app.'
        : 'Tarjeta de contacto lista para guardar.';
    } catch {
      status.textContent = 'No se pudo preparar la tarjeta de contacto.';
    } finally {
      openingContact = false;
      saveButton.disabled = expired;
    }
  });
  callActions.append(simButton, whatsappButton);
  card.append(eyebrow, title, phone, hint, callActions, saveButton, status);
  // Recordatorio de HILO: la ficha es lo último que se mira antes de llamar.
  if (!expired && configHilo()) {
    const recordatorio = document.createElement('div');
    recordatorio.className = 'notice';
    const texto = document.createElement('p');
    texto.textContent = 'Cuando llames, toca el botón de GRABAR del teléfono. Al colgar podrás subir la grabación desde la pantalla de resultado.';
    recordatorio.append(texto);
    card.append(recordatorio);
  }
  root.append(card);
}
